// TUT-551 — a ficha da pessoa da equipe: a da administração, pelo vínculo
// (`/users/:membershipId/ficha`), e a da própria pessoa, pelo Meu perfil
// (`/auth/profile/ficha`), cada uma com a foto. O contrato está em
// `docs/product/plano-tut-551-ficha-da-equipe.md`.
import { hashKey, type QueryClient, type QueryKey } from "@tanstack/react-query";

import { demoApi } from "../demo-data";
import { fotoNoPortalDaDemo } from "../demo/ficha-da-equipe";
import type { EntradaDaFichaDaEquipe, FichaDaEquipe, FotosDaEquipe } from "../types";
import { request } from "./infra";

// Lido aqui, e não importado: o empacotador só dobra a constante no módulo
// que a escreve, e é isso que tira o demo do build de produção (TUT-448).
const useDemoApi = import.meta.env.VITE_API_MODE === "demo";

const daEquipe = (membershipId: string) => `/users/${encodeURIComponent(membershipId)}/ficha`;
const PROPRIA = "/auth/profile/ficha";

export const apiDaFichaDaEquipe = {
  fichaDaEquipe(membershipId: string): Promise<FichaDaEquipe> {
    return useDemoApi ? demoApi.fichaDaEquipe(membershipId) : request<FichaDaEquipe>(daEquipe(membershipId));
  },
  /** PATCH parcial: só os campos que mudaram. */
  salvarFichaDaEquipe(membershipId: string, entrada: EntradaDaFichaDaEquipe): Promise<FichaDaEquipe> {
    return useDemoApi
      ? demoApi.salvarFichaDaEquipe(membershipId, entrada)
      : request<FichaDaEquipe>(daEquipe(membershipId), { method: "PATCH", body: JSON.stringify(entrada) });
  },
  /** O PNG do recorte da tela (o `dataUrl` só serve ao modo demonstração). */
  enviarFotoDaEquipe(membershipId: string, contentBase64: string, dataUrl: string): Promise<FichaDaEquipe> {
    return useDemoApi
      ? demoApi.enviarFotoDaEquipe(membershipId, contentBase64, dataUrl)
      : request<FichaDaEquipe>(`${daEquipe(membershipId)}/foto`, {
          method: "PUT",
          body: JSON.stringify({ contentBase64 }),
        });
  },
  removerFotoDaEquipe(membershipId: string): Promise<FichaDaEquipe> {
    return useDemoApi
      ? demoApi.removerFotoDaEquipe(membershipId)
      : request<FichaDaEquipe>(`${daEquipe(membershipId)}/foto`, { method: "DELETE" });
  },
  /** TUT-558 — as fotos da equipe interna, para os avatares de toda a equipe (não existe no portal). */
  fotosDaEquipe(): Promise<FotosDaEquipe> {
    return useDemoApi ? demoApi.fotosDaEquipe() : request<FotosDaEquipe>("/equipe/fotos");
  },
  minhaFicha(): Promise<FichaDaEquipe> {
    return useDemoApi ? demoApi.minhaFicha() : request<FichaDaEquipe>(PROPRIA);
  },
  salvarMinhaFicha(entrada: EntradaDaFichaDaEquipe): Promise<FichaDaEquipe> {
    return useDemoApi
      ? demoApi.salvarMinhaFicha(entrada)
      : request<FichaDaEquipe>(PROPRIA, { method: "PATCH", body: JSON.stringify(entrada) });
  },
  enviarMinhaFoto(contentBase64: string, dataUrl: string): Promise<FichaDaEquipe> {
    return useDemoApi
      ? demoApi.enviarMinhaFoto(contentBase64, dataUrl)
      : request<FichaDaEquipe>(`${PROPRIA}/foto`, { method: "PUT", body: JSON.stringify({ contentBase64 }) });
  },
  removerMinhaFoto(): Promise<FichaDaEquipe> {
    return useDemoApi ? demoApi.removerMinhaFoto() : request<FichaDaEquipe>(`${PROPRIA}/foto`, { method: "DELETE" });
  },
};

/**
 * A ficha da sessão no cache: o Meu perfil a edita, e o portão (`App.tsx`) lê
 * a foto dela para o menu da conta. Mora aqui, e não na tela, para o portão
 * não puxar o formulário para o pacote de entrada.
 */
export const CHAVE_DA_MINHA_FICHA = ["minha-ficha"] as const;

/** A ficha que a administração abre, pelo vínculo: a mesma para a leitura e para o que as escritas devolvem. */
export const chaveDaFichaDaEquipe = (membershipId: string) => ["ficha-da-equipe", membershipId] as const;

/**
 * TUT-552 — a equipe INTEIRA (`GET /users` sem página), que só quem tem
 * `usuarios.gerir` lê: Grupos a usa, e as fotos dos avatares saem dela.
 * Começa por `"users"`: quem invalida a lista de Usuários a leva junto.
 */
export const CHAVE_DA_EQUIPE_INTEIRA = ["users", "equipe-inteira"] as const;

/**
 * TUT-558 — as fotos da equipe (`GET /equipe/fotos`), a fonte de todos os
 * avatares fora de Usuários, para qualquer pessoa da equipe.
 */
export const CHAVE_DAS_FOTOS_DA_EQUIPE = ["fotos-da-equipe"] as const;

/**
 * TUT-558 (decisão do dono em 29/10) — a foto de uma pessoa da equipe para
 * quem acessa pelo PORTAL, só onde o nome dela já aparece para ele: em quem
 * respondeu na conversa com o escritório. Pelo id de quem escreveu; sem
 * versão na URL (o portal não tem lista da equipe), e o navegador revalida
 * pelo `ETag`. Sem foto, a API responde 404 e o avatar fica nas iniciais.
 */
export function urlDaFotoNoPortal(userId: string): string | null {
  return useDemoApi ? fotoNoPortalDaDemo(userId) : `/api/portal/equipe/${encodeURIComponent(userId)}/foto`;
}

/** As raízes das chaves que mostram a ficha (ou a foto) de alguém. */
const RAIZES_DA_FICHA = new Set<unknown>([
  CHAVE_DA_MINHA_FICHA[0],
  "ficha-da-equipe",
  CHAVE_DA_EQUIPE_INTEIRA[0],
  CHAVE_DAS_FOTOS_DA_EQUIPE[0],
]);

/**
 * TUT-552 — depois de toda escrita na ficha ou na foto, de qualquer lado: a
 * mesma pessoa aparece na Minha ficha (e no menu da conta), na ficha que a
 * administração abre e na lista de Usuários (o avatar, o nome de tratamento e
 * o cargo) — e, desde a TUT-558, nas fotos da equipe dos avatares. Quem grava
 * não sabe se a ficha é a de quem está logado, e por isso invalida as quatro;
 * só a consulta ativa relê na hora, as outras na próxima vez.
 *
 * `gravada` é a chave em que quem chama já pôs a resposta da escrita: ela é a
 * verdade do servidor, e reler essa seria uma ida à toa.
 */
export function invalidarFichas(queryClient: QueryClient, gravada?: QueryKey): Promise<void> {
  const pulada = gravada ? hashKey(gravada) : null;
  return queryClient.invalidateQueries({
    predicate: ({ queryKey, queryHash }) =>
      RAIZES_DA_FICHA.has(queryKey[0]) && queryHash !== pulada,
  });
}

